// KeyValueSelect component
import React, { useMemo, useRef, useState } from 'react';
import { KeySelect, ValueSelect, KeyValueSelectContainer } from './styled';
import Icon from '@ant-design/icons';
import TrashIcon from '@src/icons/trash.svg';

interface OptionItem {
  label?: React.ReactNode;
  value: string;
  name?: string;
}

interface OperatorOption {
  label: string;
  value: string;
}
interface KeyValueSelectProps {
  keyOptions: OptionItem[];
  valueOptions: OptionItem[];
  operatorOptions?: OperatorOption[];
  onChange: (key: string | undefined, values: (string | OptionItem)[], operator?: string) => void;
  // Controlled values (optional)
  selectedKey?: string;
  selectedValues?: (string | OptionItem)[];
  selectedOperator?: string;
  // UI controls
  disableKey?: boolean;
  disableValues?: boolean;
  disableOperator?: boolean;
  loadingKey?: boolean;
  loadingValues?: boolean;
  hideKey?: boolean;
  hideOperator?: boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  onDelete?: () => void;
  invalidKeyPlaceholder?: React.ReactNode;
  showOperator?: boolean;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  operatorPlaceholder?: string;
  keyStyle?: React.CSSProperties;
  onSearch?: (key: string | undefined, values: any[], operator?: string) => void;
  onDropdownVisibleChange?: (open: boolean) => void;
  keepValuesOnKeyChange?: boolean;
  labelInValue?: boolean;
  onBlur?: () => void;
  mode?: 'multiple' | 'tags' | undefined;
}
const DEFAULT_OPERATOR_OPTIONS = [
  { label: 'Equals', value: 'equals' },
  { label: 'Contains', value: 'contains' },
  // { label: 'Starts With', value: 'startwith' },
  // { label: 'Ends With', value: 'endwith' }
];
const KeyValueSelect: React.FC<KeyValueSelectProps> = ({
  keyOptions,
  valueOptions,
  operatorOptions,
  keyPlaceholder = 'Select Key',
  valuePlaceholder = 'Select Value',
  onChange,
  selectedKey,
  selectedValues,
  selectedOperator,
  disableKey,
  disableValues,
  disableOperator,
  loadingKey,
  loadingValues,
  hideKey,
  invalidKeyPlaceholder,
  getPopupContainer,
  onDelete,
  showOperator = true,
  keyStyle,
  onSearch,
  onDropdownVisibleChange,
  keepValuesOnKeyChange = false,
  labelInValue = false,
  onBlur,
  mode,
}) => {
  // Uncontrolled fallbacks
  const [internalKey, setInternalKey] = useState<string | undefined>(undefined);
  const [internalValues, setInternalValues] = useState<(string | OptionItem)[]>([]);
  const [internalOperator, setInternalOperator] = useState<string>('equals');

  // Track if dropdown is open to differentiate tag removal from dropdown deselect
  const isDropdownOpenRef = useRef(false);

  const keyValue = useMemo(() => {
    const key = selectedKey !== undefined ? selectedKey : internalKey;
    // Treat empty strings as undefined for placeholder display
    return key === '' ? undefined : key;
  }, [selectedKey, internalKey]);

  const valuesValue = useMemo(
    () => (selectedValues !== undefined ? selectedValues : internalValues),
    [selectedValues, internalValues],
  );

  const operatorValue = useMemo(() => {
    return selectedOperator !== undefined ? selectedOperator : internalOperator;
  }, [selectedOperator, internalOperator]);

  return (
    <KeyValueSelectContainer>
      {!hideKey && (
        <>
          <KeySelect
            popupMatchSelectWidth={false}
            options={keyOptions}
            value={keyValue}
            onChange={(value) => {
              const newKey = value as string;
              if (selectedKey === undefined) {
                setInternalKey(newKey);
              }
              // reset values when key changes
              if (selectedValues === undefined) {
                setInternalValues([]);
              }
              onChange(newKey, keepValuesOnKeyChange ? selectedValues || [] : [], operatorValue);
            }}
            labelRender={(p) =>
              keyOptions.find((opt) => opt.value === p.value)?.label ??
              invalidKeyPlaceholder ??
              p.value
            }
            disabled={disableKey}
            loading={loadingKey}
            placeholder={keyPlaceholder}
            getPopupContainer={getPopupContainer}
            className="key-value-dropdown"
            style={{ ...keyStyle }}
            showSearch={true}
            filterOption={(input, option) => {
              const label = option?.label;
              const name = option?.name;
              if (typeof label === 'string') {
                return label.toLowerCase().includes(input.toLowerCase());
              }
              if (name && name.toLowerCase().includes(input.toLowerCase())) {
                return true;
              }
              return false;
            }}
          />
          {showOperator && (
            <KeySelect
              popupMatchSelectWidth={false}
              options={operatorOptions || DEFAULT_OPERATOR_OPTIONS}
              value={operatorValue}
              onChange={(value) => {
                const newOperator = value as string;
                if (selectedOperator === undefined) {
                  setInternalOperator(newOperator);
                }
                onChange(keyValue, valuesValue, newOperator);
              }}
              disabled={disableOperator}
              placeholder={'Select Operator'}
              getPopupContainer={getPopupContainer}
              className="key-value-dropdown operator-select"
              style={{ width: 'fit-content', minWidth: '80px', borderRadius: '0' }}
            />
          )}
        </>
      )}
      <ValueSelect
        popupMatchSelectWidth={false}
        labelInValue={labelInValue}
        options={valueOptions}
        value={valuesValue}
        selectedCount={selectedValues?.length}
        $minWidth={'120px'}
        onChange={(vals) => {
          const newValues = (vals as (string | OptionItem)[]) || [];
          if (selectedValues === undefined) {
            setInternalValues(newValues);
          }
          onChange(keyValue, newValues, operatorValue);
        }}
        onDeselect={() => {
          // Only trigger onBlur when dropdown is closed (clicking X on tag)
          // Not when deselecting from dropdown
          if (!isDropdownOpenRef.current) {
            setTimeout(() => {
              onBlur?.();
            }, 200);
          }
        }}
        onClear={() => {
          // When all values are cleared via the clear button, trigger onBlur
          // Only if dropdown is closed
          if (!isDropdownOpenRef.current) {
            setTimeout(() => {
              onBlur?.();
            }, 200);
          }
        }}
        onBlur={onBlur}
        onClick={() => {
          onSearch?.(keyValue, [], operatorValue);
        }}
        mode={mode || 'tags'}
        maxTagCount={1}
        disabled={disableValues}
        loading={loadingValues}
        placeholder={valuePlaceholder}
        allowClear
        getPopupContainer={getPopupContainer}
        className="key-value-dropdown"
        $showRightBorder={disableKey}
        showSearch={true}
        onSearch={(value) => {
          onSearch?.(keyValue, [value], operatorValue);
        }}
        onOpenChange={(open) => {
          isDropdownOpenRef.current = open;
          onDropdownVisibleChange?.(open);
        }}
        filterOption={(input, option) => {
          const label = option?.label;
          const name = option?.name;
          const value = option?.value;
          const inputLower = input.toLowerCase();

          if (typeof label === 'string' && label.toLowerCase().includes(inputLower)) {
            return true;
          }
          if (name && name.toLowerCase().includes(inputLower)) {
            return true;
          }
          if (typeof value === 'string' && value.toLowerCase().includes(inputLower)) {
            return true;
          }
          return false;
        }}
      />
      {onDelete && (
        <div className="delete-icon-container">
          <Icon
            component={TrashIcon as unknown as React.FC<React.SVGProps<SVGSVGElement>>}
            className="delete-icon"
            onClick={onDelete}
            data-testid="dimension-filter-delete"
          />
        </div>
      )}
    </KeyValueSelectContainer>
  );
};

export default KeyValueSelect;
